import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Radio, Select, Spin, Table, Tooltip } from 'antd';
import { CopyOutlined, DeleteOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { decode as decodeHtml } from 'he';

import { storageItemApi } from '../../../../../apis';
import {
  BulkCreateSpecialHandlingFeeDto,
  BulkSpecialHandlingFeeResult,
  BulkSpecialHandlingFeeResultRow,
  StorageItem,
} from '../../../../../apis/client-axios';
import CustomInput from '../../../../../components/input/CustomInput';
import { CustomCommonButton } from '../../../../../components/buttons/common/CustomButton';
import NotificationError from '../../../../../components/notifications/NotificationError';
import './index.scss';

/**
 * Task 5/6 — 手数料一括追加 (商品管理 → 一括操作).
 * Bảng nhiều dòng, mỗi dòng là một cặp (item, khoản phí) ĐỘC LẬP — cùng một item được phép lặp ở
 * nhiều dòng với khoản phí khác nhau (item1/phí1, item1/phí2, item2/phí3).
 * Mỗi dòng = 1 charge PayJP riêng (BE chạy tuần tự, không dừng khi 1 dòng lỗi).
 * 3 bước trong cùng modal: edit → confirm → result.
 */

type FeeRow = {
  key: number;
  item: StorageItem | null;
  title: string;
  chargeAmount: string;
  isProfit: boolean;
};

type RowError = { item: boolean; title: boolean; chargeAmount: boolean };

type Stage = 'edit' | 'confirm' | 'result';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  /** Gọi khi có ít nhất 1 dòng charge thành công (để refetch danh sách). */
  onSuccess?: () => void;
};

const ERROR_JP: Record<string, string> = {
  NO_PAYMENT_METHOD: '支払い方法が登録されていません',
  CHARGE_MONEY_FAILED: '決済に失敗しました',
  STORAGE_ITEM_NOT_FOUND: '商品が見つかりません',
  TITLE_REQUIRED: '手数料名が未入力です',
  CHARGE_AMOUNT_MUST_BE_POSITIVE: '金額が無効です',
};

const errorToJp = (code?: string) => (code ? ERROR_JP[code] ?? code : '');

const SEARCH_PAGE_SIZE = 20;

const emptyRow = (key: number): FeeRow => ({ key, item: null, title: '', chargeAmount: '', isProfit: true });

const itemLabel = (item: StorageItem) => `${item.internalSku ?? ''}｜${decodeHtml(item.itemName || '')}`;

/**
 * Ô chọn item của một dòng. Mỗi ô tự tìm kiếm riêng; `queryKey` chỉ phụ thuộc từ khoá nên các ô
 * gõ cùng từ khoá dùng chung cache. Chỉ gọi API khi dropdown mở (tránh N request lúc mới mở modal).
 */
const ItemSelectCell: React.FC<{
  value: StorageItem | null;
  hasError?: boolean;
  onChange: (item: StorageItem) => void;
}> = ({ value, hasError, onChange }) => {
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchText.trim()), 400);
    return () => clearTimeout(t);
  }, [searchText]);

  const { data, isFetching } = useQuery({
    queryKey: ['bulkSpecialFee', 'searchItems', debouncedSearch],
    queryFn: () =>
      storageItemApi.storageItemControllerItemManagementAdmin(
        1,
        SEARCH_PAGE_SIZE,
        null,
        debouncedSearch || undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        'ALL',
      ),
    enabled: open,
    staleTime: 5000,
  });

  const options = useMemo(() => {
    const results = (data?.data?.content as StorageItem[]) ?? [];
    // Item đang chọn luôn có mặt để Select hiển thị đúng nhãn kể cả khi nó không nằm trong kết quả tìm
    const merged = value && !results.some((r) => r.id === value.id) ? [value, ...results] : results;
    return merged.map((item) => ({
      value: item.id,
      label: itemLabel(item),
      customer: `${item.storageRequest?.user?.name ?? ''} ${item.storageRequest?.user?.userCode ?? ''}`.trim(),
      item,
    }));
  }, [data, value]);

  return (
    <Select
      className="bulk-special-fee__item-select"
      showSearch
      filterOption={false}
      placeholder="SKU・商品名で検索"
      status={hasError ? 'error' : undefined}
      value={value?.id}
      options={options}
      onSearch={setSearchText}
      onDropdownVisibleChange={setOpen}
      onSelect={(id) => {
        const picked = options.find((o) => o.value === Number(id));
        if (picked) onChange(picked.item);
      }}
      optionRender={(option) => (
        <div className="bulk-special-fee__option">
          <span className="bulk-special-fee__option-main">{option.data.label}</span>
          {option.data.customer && <span className="bulk-special-fee__option-sub">{option.data.customer}</span>}
        </div>
      )}
      notFoundContent={isFetching ? <Spin size="small" /> : '該当する商品がありません'}
      popupClassName="bulk-special-fee__dropdown"
      popupMatchSelectWidth={420}
    />
  );
};

export const BulkSpecialFeeModal: React.FC<Props> = ({ isOpen, onClose, onSuccess }) => {
  const intl = useIntl();

  const [stage, setStage] = useState<Stage>('edit');
  const [rows, setRows] = useState<FeeRow[]>([emptyRow(1)]);
  const [rowErrors, setRowErrors] = useState<Record<number, RowError>>({});
  const [result, setResult] = useState<BulkSpecialHandlingFeeResult | null>(null);
  const [nextKey, setNextKey] = useState(2);

  useEffect(() => {
    if (isOpen) {
      setStage('edit');
      setRows([emptyRow(1)]);
      setRowErrors({});
      setResult(null);
      setNextKey(2);
    }
  }, [isOpen]);

  const updateRow = (key: number, patch: Partial<FeeRow>) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
    setRowErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev[key] };
      if (patch.item !== undefined) next.item = false;
      if (patch.title !== undefined) next.title = false;
      if (patch.chargeAmount !== undefined) next.chargeAmount = false;
      return { ...prev, [key]: next };
    });
  };

  const addRow = () => {
    setRows((prev) => [...prev, emptyRow(nextKey)]);
    setNextKey((k) => k + 1);
  };

  /** Nhân bản dòng: chèn bản sao ngay dưới dòng gốc — nhập nhanh nhiều khoản phí cho cùng một item. */
  const duplicateRow = (key: number) => {
    setRows((prev) => {
      const idx = prev.findIndex((r) => r.key === key);
      if (idx < 0) return prev;
      const copy: FeeRow = { ...prev[idx], key: nextKey };
      return [...prev.slice(0, idx + 1), copy, ...prev.slice(idx + 1)];
    });
    setNextKey((k) => k + 1);
  };

  const removeRow = (key: number) => {
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.key !== key)));
    setRowErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const grandTotal = useMemo(
    () => rows.reduce((sum, r) => sum + (Number.isFinite(Number(r.chargeAmount)) ? Number(r.chargeAmount) : 0), 0),
    [rows],
  );

  const validateRows = () => {
    const errors: Record<number, RowError> = {};
    let ok = true;
    for (const r of rows) {
      const amount = Number(r.chargeAmount.trim());
      const e: RowError = {
        item: !r.item,
        title: !r.title.trim(),
        chargeAmount: !r.chargeAmount.trim() || !Number.isFinite(amount) || amount <= 0,
      };
      if (e.item || e.title || e.chargeAmount) {
        ok = false;
        errors[r.key] = e;
      }
    }
    setRowErrors(errors);
    return ok;
  };

  const handleGoConfirm = () => {
    if (!validateRows()) {
      NotificationError({ content: '未入力の項目があります。' });
      return;
    }
    setStage('confirm');
  };

  const bulkMutation = useMutation({
    mutationFn: (dto: BulkCreateSpecialHandlingFeeDto) =>
      storageItemApi.storageItemControllerAddSpecialHandlingFeeBulk(dto),
    onSuccess: ({ data }) => {
      setResult(data);
      setStage('result');
      if ((data?.succeeded ?? 0) > 0) onSuccess?.();
    },
    onError: () => {
      NotificationError({ content: '登録に失敗しました。' });
      setStage('edit');
    },
  });

  const handleSubmit = () => {
    if (bulkMutation.isPending) return;
    bulkMutation.mutate({
      lines: rows.map((r) => ({
        storageItemId: r.item!.id,
        title: r.title.trim(),
        chargeAmount: Number(r.chargeAmount.trim()),
        isProfit: r.isProfit,
      })),
    });
  };

  const handleClose = () => {
    if (bulkMutation.isPending) return;
    onClose();
  };

  // ───────────────────────── columns ─────────────────────────
  const editColumns = [
    {
      title: (
        <span>
          商品<span className="bulk-special-fee__required">*</span>
        </span>
      ),
      key: 'item',
      width: 320,
      render: (_: unknown, row: FeeRow) => (
        <div className="bulk-special-fee__field">
          <ItemSelectCell
            value={row.item}
            hasError={rowErrors[row.key]?.item}
            onChange={(item) => updateRow(row.key, { item })}
          />
          {rowErrors[row.key]?.item && <span className="bulk-special-fee__error">商品を選択してください。</span>}
        </div>
      ),
    },
    {
      title: (
        <span>
          手数料名<span className="bulk-special-fee__required">*</span>
        </span>
      ),
      key: 'title',
      render: (_: unknown, row: FeeRow) => (
        <div className="bulk-special-fee__field">
          <CustomInput
            key={`title-${row.key}`}
            placeholder="例: 梱包手数料"
            height={40}
            status={rowErrors[row.key]?.title ? 'error' : undefined}
            value={row.title}
            onChange={(e) => updateRow(row.key, { title: e.target.value })}
          />
          {rowErrors[row.key]?.title && <span className="bulk-special-fee__error">手数料名を入力してください。</span>}
        </div>
      ),
    },
    {
      title: (
        <span>
          金額 ($)<span className="bulk-special-fee__required">*</span>
        </span>
      ),
      key: 'chargeAmount',
      width: 140,
      render: (_: unknown, row: FeeRow) => (
        <div className="bulk-special-fee__field">
          <CustomInput
            key={`amount-${row.key}`}
            type="number"
            placeholder="0.00"
            height={40}
            min={0}
            status={rowErrors[row.key]?.chargeAmount ? 'error' : undefined}
            value={row.chargeAmount}
            onKeyDown={(e) => (['e', 'E', '+', '-'].includes(e.key) ? e.preventDefault() : null)}
            onChange={(e) => updateRow(row.key, { chargeAmount: e.target.value })}
          />
          {rowErrors[row.key]?.chargeAmount && (
            <span className="bulk-special-fee__error">金額を入力してください。</span>
          )}
        </div>
      ),
    },
    {
      title: '粗利区分',
      key: 'isProfit',
      width: 250,
      render: (_: unknown, row: FeeRow) => (
        <Radio.Group value={row.isProfit} onChange={(e) => updateRow(row.key, { isProfit: e.target.value })}>
          <Radio value={true}>粗利対象</Radio>
          <Radio value={false}>粗利対象外</Radio>
        </Radio.Group>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      render: (_: unknown, row: FeeRow) => (
        <div className="bulk-special-fee__row-actions">
          <Tooltip title="この行を複製">
            <button type="button" className="bulk-special-fee__icon-btn" onClick={() => duplicateRow(row.key)}>
              <CopyOutlined />
            </button>
          </Tooltip>
          <Tooltip title="この行を削除">
            <button
              type="button"
              className="bulk-special-fee__icon-btn is-danger"
              disabled={rows.length <= 1}
              onClick={() => removeRow(row.key)}
            >
              <DeleteOutlined />
            </button>
          </Tooltip>
        </div>
      ),
    },
  ];

  const confirmColumns = [
    {
      title: '商品',
      key: 'item',
      width: 320,
      render: (_: unknown, row: FeeRow) => <span className="text-2-line">{row.item ? itemLabel(row.item) : ''}</span>,
    },
    { title: '手数料名', dataIndex: 'title', key: 'title' },
    {
      title: '金額',
      key: 'chargeAmount',
      width: 120,
      render: (_: unknown, row: FeeRow) => `$${Number(row.chargeAmount).toFixed(2)}`,
    },
    {
      title: '粗利区分',
      key: 'isProfit',
      width: 120,
      render: (_: unknown, row: FeeRow) => (row.isProfit ? '粗利対象' : '粗利対象外'),
    },
  ];

  const resultColumns = [
    {
      title: '結果',
      key: 'success',
      width: 80,
      render: (_: unknown, r: BulkSpecialHandlingFeeResultRow) => (
        <span className={`bulk-special-fee__result-tag ${r.success ? 'is-ok' : 'is-ng'}`}>
          {r.success ? '成功' : '失敗'}
        </span>
      ),
    },
    { title: 'BayPack管理番号', dataIndex: 'internalSku', key: 'internalSku', width: 180 },
    {
      title: intl.formatMessage({ id: 'item.productName' }),
      dataIndex: 'itemName',
      key: 'itemName',
      render: (v: string) => <span className="text-2-line">{decodeHtml(v || '')}</span>,
    },
    { title: '手数料名', dataIndex: 'title', key: 'title', width: 160 },
    {
      title: '金額',
      dataIndex: 'chargeAmount',
      key: 'chargeAmount',
      width: 100,
      render: (v: number) => `$${Number(v ?? 0).toFixed(2)}`,
    },
    {
      title: '粗利区分',
      dataIndex: 'isProfit',
      key: 'isProfit',
      width: 100,
      render: (v: boolean) => (v ? '粗利対象' : '粗利対象外'),
    },
    {
      title: 'エラー',
      dataIndex: 'error',
      key: 'error',
      width: 200,
      render: (v: string) => <span className="bulk-special-fee__error-text">{errorToJp(v)}</span>,
    },
  ];

  const summaryText = `合計 ${rows.length}件　$${grandTotal.toFixed(2)}`;

  // ───────────────────────── render ─────────────────────────
  return (
    <Modal
      title=""
      closable={false}
      maskClosable={false}
      open={isOpen}
      onCancel={handleClose}
      className="bulk-special-fee-modal"
      centered
      destroyOnClose
      footer={null}
      width={1200}
    >
      {stage === 'edit' && (
        <>
          <p className="bulk-special-fee__title">手数料一括追加</p>
          <p className="bulk-special-fee__hint">
            1行 = 1商品 1手数料です。同じ商品に複数の手数料を付ける場合は、行を複製して手数料名を変更してください。
          </p>

          <Table
            className="bulk-special-fee__table bulk-special-fee__table--rows"
            size="small"
            rowKey="key"
            pagination={false}
            columns={editColumns as any}
            dataSource={rows}
            scroll={{ y: 380 }}
          />

          <div className="bulk-special-fee__toolbar">
            <button type="button" className="bulk-special-fee__add-line" onClick={addRow}>
              ＋ 行追加
            </button>
            <span className="bulk-special-fee__summary">{summaryText}</span>
          </div>

          <div className="bulk-special-fee__footer">
            <CustomCommonButton
              isCancelButton={true}
              buttonProps={{ className: 'button-small', onClick: handleClose }}
              content={intl.formatMessage({ id: 'common.cancel' })}
            />
            <CustomCommonButton
              buttonProps={{ className: 'button-small', type: 'primary', onClick: handleGoConfirm }}
              content={intl.formatMessage({ id: 'common.add' })}
            />
          </div>
        </>
      )}

      {stage === 'confirm' && (
        <>
          <p className="bulk-special-fee__title">手数料一括追加の確認</p>
          <p className="bulk-special-fee__confirm-note">
            以下の内容で即時決済を行います（{rows.length}件の決済）。決済後の取り消しはできません。
          </p>
          <Table
            className="bulk-special-fee__table"
            size="small"
            rowKey="key"
            pagination={false}
            columns={confirmColumns as any}
            dataSource={rows}
            scroll={{ y: 320 }}
          />
          <div className="bulk-special-fee__summary bulk-special-fee__summary--block">{summaryText}</div>

          <div className="bulk-special-fee__footer">
            <CustomCommonButton
              isCancelButton={true}
              buttonProps={{
                className: 'button-small',
                onClick: () => setStage('edit'),
                disabled: bulkMutation.isPending,
              }}
              content="戻る"
            />
            <CustomCommonButton
              buttonProps={{
                className: 'button-small',
                type: 'primary',
                onClick: handleSubmit,
                disabled: bulkMutation.isPending,
                loading: bulkMutation.isPending,
              }}
              content="請求する"
            />
          </div>
        </>
      )}

      {stage === 'result' && result && (
        <>
          <p className="bulk-special-fee__title">手数料一括追加の結果</p>
          <div className="bulk-special-fee__result-summary">
            <span>合計 {result.total}件</span>
            <span className="is-ok">成功 {result.succeeded}件</span>
            <span className={result.failed > 0 ? 'is-ng' : ''}>失敗 {result.failed}件</span>
          </div>
          {result.failed > 0 && (
            <p className="bulk-special-fee__result-note">
              失敗した行は決済されていません。原因を確認のうえ、該当商品で再度登録してください。
            </p>
          )}
          <Table
            className="bulk-special-fee__table"
            size="small"
            rowKey={(r: BulkSpecialHandlingFeeResultRow, idx) => `${r.storageItemId}-${idx}`}
            pagination={false}
            columns={resultColumns as any}
            dataSource={result.results}
            scroll={{ y: 360 }}
          />
          <div className="bulk-special-fee__footer">
            <CustomCommonButton
              buttonProps={{ className: 'button-small', type: 'primary', onClick: onClose }}
              content="閉じる"
            />
          </div>
        </>
      )}
    </Modal>
  );
};

export default BulkSpecialFeeModal;
